// react
import React from 'react';
import styled from 'styled-components';

// components
import { Container, Title } from '@/components/atoms';
import { Editor } from '@/components/organisms';
import { Content } from '@/components/templates';

// editor
import { FieldDef } from '@/components/organisms/EditorFields/fieldHelper';

/**
 * EditorPage is an editor for creating a new care profile.
 */
export const EditorPage = ({
  fields,
  value,
  onChange,
  title,
  description,
  onSave,
  onCancel,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  isSaveDisabled,
  start,
  end,
}: EditorPageProps) => {
  return (
    <Content
      content={
        <Container size='sm'>
          <Wrapper>
            <Header>
              <Title>{title}</Title>
              {description && <Description>{description}</Description>}
            </Header>
            {start}
            <Editor
              fields={fields}
              value={value}
              onChange={onChange}
              saveLabel={saveLabel}
              cancelLabel={cancelLabel}
              onSave={onSave}
              onCancel={onCancel}
              isSaveDisabled={isSaveDisabled}
              isWithArrows
            />
            {end}
          </Wrapper>
        </Container>
      }
    />
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
`;

const Description = styled.div`
  font: ${(props) => props.theme.typography.heading['3xl']};
  color: ${(props) => props.theme.color.neutral[90]};
`;

interface EditorPageProps<T = any> {
  fields: { [key in keyof T]: FieldDef };
  value: T;
  onChange: (value: T) => void;
  title: string;
  description?: string;
  onSave: () => void;
  onCancel: () => void;
  saveLabel?: string;
  cancelLabel?: string;
  isSaveDisabled?: boolean;
  start?: React.ReactNode;
  end?: React.ReactNode;
}

EditorPage.displayName = 'EditorPage';

export default EditorPage;
